import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Skeleton,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GavelIcon from "@mui/icons-material/Gavel";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import api from "../../utils/api";
import { ModulesContext, isModuleActive } from "../../utils/ModulesContext";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// ── Shared constants ─────────────────────────────────────────────────────────
const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const softColors = [
  "#A3E4D7", "#F9E79F", "#AED6F1", "#F5B7B1", "#D2B4DE",
  "#A9CCE3", "#FAD7A0", "#A2D9CE", "#F5CBA7", "#BB8FCE",
];

const MODULE_COLORS = {
  workOrders:  "rgba(47,99,173,0.75)",
  violations:  "rgba(220,38,38,0.72)",
  archRequests:"rgba(34,197,94,0.72)",
  billing:     "rgba(249,115,22,0.72)",
};

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_LABELS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const RESOLUTION_BUCKETS = ["Same day","1-3 days","4-7 days","8-30 days","31-90 days","90+ days"];

const CLOSED_STATUSES = new Set([
  "closed","resolved","violation cured","withdrawn / canceled","approved","completed",
]);

// ── Small helpers ─────────────────────────────────────────────────────────────
function todayISO() { return new Date().toISOString().slice(0, 10); }
function ytdStart()  { return `${new Date().getFullYear()}-01-01`; }
function addDaysISO(s, n) {
  if (!s) return s;
  const d = new Date(s + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function buildMonthlyKey(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function last12MonthKeys() {
  const keys = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
}

function bucketDays(days) {
  if (days <= 0) return 0;
  if (days <= 3) return 1;
  if (days <= 7) return 2;
  if (days <= 30) return 3;
  if (days <= 90) return 4;
  return 5;
}

function processYearlyMonthly(data, dateField) {
  const result = {};
  data.forEach((item) => {
    const d = new Date(item[dateField]);
    if (isNaN(d)) return;
    const yr = d.getFullYear();
    const mo = d.getMonth();
    if (!result[yr]) result[yr] = new Array(12).fill(0);
    result[yr][mo]++;
  });
  return result;
}

function getMostCommonTypeByMonthByYear(data) {
  const result = {};
  data.forEach((item) => {
    const d = new Date(item.created_at);
    if (isNaN(d)) return;
    const yr = d.getFullYear();
    const mo = d.getMonth();
    const type = item.type_name || item.type || "Unknown";
    if (!result[yr]) result[yr] = new Array(12).fill(null).map(() => ({}));
    result[yr][mo][type] = (result[yr][mo][type] || 0) + 1;
  });
  const formatted = {};
  Object.keys(result).forEach((yr) => {
    formatted[yr] = result[yr].map((monthData) => {
      const entries = Object.entries(monthData);
      if (!entries.length) return "N/A";
      return entries.sort((a, b) => b[1] - a[1])[0][0];
    });
  });
  return formatted;
}

function forecastNextMonth(monthlyArr) {
  const mo = new Date().getMonth();
  const prev3 = [
    monthlyArr[(mo - 2 + 12) % 12],
    monthlyArr[(mo - 1 + 12) % 12],
    monthlyArr[mo],
  ].filter((v) => typeof v === "number");
  if (!prev3.length) return 0;
  return Math.round(prev3.reduce((s, v) => s + v, 0) / prev3.length);
}

// ── Shared chart option factories ─────────────────────────────────────────────
const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "55%",
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 12, padding: 10, font: { weight: "600" } } },
    datalabels: {
      formatter: (value, ctx) => {
        const total = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0) || 1;
        const pct = Math.round((value / total) * 100);
        return pct >= 6 ? `${pct}%` : "";
      },
      color: "#223142",
      backgroundColor: "rgba(255,255,255,0.92)",
      borderColor: "rgba(0,0,0,0.12)",
      borderWidth: 1,
      borderRadius: 6,
      padding: 5,
      font: { weight: "bold", size: 11 },
    },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0) || 1;
          const pct = Math.round((ctx.raw / total) * 100);
          return `${ctx.label}: ${ctx.raw} (${pct}%)`;
        },
      },
    },
  },
};

const hBarOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    datalabels: {
      color: "#223142",
      backgroundColor: "rgba(255,255,255,0.92)",
      borderColor: "rgba(0,0,0,0.12)",
      borderWidth: 1,
      borderRadius: 4,
      font: { weight: "bold", size: 11 },
      padding: 4,
      anchor: "end",
      align: "right",
      clip: false,
      display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
    },
  },
  scales: {
    x: { beginAtZero: true, grid: { color: "rgba(47,99,173,0.08)" } },
    y: { grid: { display: false } },
  },
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom" },
    datalabels: { display: false },
  },
  scales: {
    x: { grid: { color: "rgba(47,99,173,0.06)" } },
    y: { beginAtZero: true, grid: { color: "rgba(47,99,173,0.08)" } },
  },
};

const stackedBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom" },
    datalabels: { display: false },
  },
  scales: {
    x: { stacked: true, grid: { display: false } },
    y: { stacked: true, beginAtZero: true, grid: { color: "rgba(47,99,173,0.08)" } },
  },
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const GlassCard = ({ children, sx, ...rest }) => (
  <Card elevation={0} sx={{ ...adminCardSx, p: { xs: 2, md: 3 }, ...sx }} {...rest}>
    {children}
  </Card>
);

const SectionHeader = ({ title, subtitle, right }) => (
  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: 1.5, mb: 2 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: subtitle ? 0.5 : 0 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" sx={{ color: "#6b778c" }}>{subtitle}</Typography>}
    </Box>
    {right}
  </Box>
);

const KpiCard = ({ label, value, sublabel, loading, color = "#2f63ad", icon }) => (
  <GlassCard sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", alignItems: "center", gap: 1.5, transition: "all 0.2s ease", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 18px rgba(0,0,0,0.18)" } }}>
    {/* Crystal icon box */}
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        flexShrink: 0,
        background: `${color}18`,
        backdropFilter: "blur(8px)",
        border: `1px solid ${color}35`,
        boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      {loading ? (
        <>
          <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="40%" height={24} />
        </>
      ) : (
        <>
          <Typography sx={{ textTransform: "uppercase", fontSize: 9, fontWeight: 600, color: "#6b778c", lineHeight: 1 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2, color: "#1f2d3d", mt: 0.2 }}>{value}</Typography>
          {sublabel && <Typography variant="caption" sx={{ color: "#8a94a6", fontWeight: 500 }}>{sublabel}</Typography>}
        </>
      )}
    </Box>
  </GlassCard>
);

const ModuleSection = ({ title, icon: Icon, accentColor, bgColor, children }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      px: 2.5, py: 1.5, mb: 2,
      borderRadius: "14px",
      backgroundColor: bgColor,
      borderLeft: `5px solid ${accentColor}`,
    }}>
      <Icon sx={{ color: accentColor, fontSize: 22 }} />
      <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142" }}>{title}</Typography>
    </Box>
    {children}
  </Box>
);

// ═══════════════════════════════════════════════════════════════════════════════
const OverviewWorkOrders = () => {
  const { modules } = useContext(ModulesContext);
  const woActive   = isModuleActive(modules, 1);
  const vioActive  = isModuleActive(modules, 2);
  const archActive = isModuleActive(modules, 3);

  const [filter, setFilter] = useState({ startDate: ytdStart(), endDate: todayISO() });
  const [loadingWO, setLoadingWO]     = useState(true);
  const [loadingVio, setLoadingVio]   = useState(true);
  const [loadingArch, setLoadingArch] = useState(true);
  const [loadingBill, setLoadingBill] = useState(true);

  const [workOrdersData, setWorkOrdersData]   = useState([]);
  const [violationsData, setViolationsData]   = useState([]);
  const [archData, setArchData]               = useState([]);
  const [billingData, setBillingData]         = useState([]);
  const [workOrderTypes, setWorkOrderTypes]   = useState({});

  const [woError, setWoError]     = useState(false);
  const [vioError, setVioError]   = useState(false);
  const [archError, setArchError] = useState(false);
  const [billError, setBillError] = useState(false);

  const [viewMode, setViewMode] = useState("all");

  // ── Fetch all modules in parallel ────────────────────────────────────────────
  useEffect(() => {
    const { startDate, endDate } = filter;
    const endPlus = addDaysISO(endDate, 1);

    // Work Orders
    setLoadingWO(true);
    setWoError(false);
    api.get("/getWorkOrders")
      .then(({ data }) => setWorkOrdersData(data || []))
      .catch(() => { setWorkOrdersData([]); setWoError(true); })
      .finally(() => setLoadingWO(false));

    // Violations
    setLoadingVio(true);
    setVioError(false);
    const vioParams = new URLSearchParams({ includeMember: "1" });
    if (startDate) vioParams.append("start", startDate);
    if (endDate)   vioParams.append("end", endPlus);
    api.get(`/getViolationsReport?${vioParams}`)
      .then(({ data }) => setViolationsData(data || []))
      .catch(() => { setViolationsData([]); setVioError(true); })
      .finally(() => setLoadingVio(false));

    // Architectural Requests
    setLoadingArch(true);
    setArchError(false);
    const archParams = new URLSearchParams({ includeMember: "1" });
    if (startDate) archParams.append("start", startDate);
    if (endDate)   archParams.append("end", endPlus);
    api.get(`/getArchitecturalRequestsReport?${archParams}`)
      .then(({ data }) => setArchData(data || []))
      .catch(() => { setArchData([]); setArchError(true); })
      .finally(() => setLoadingArch(false));

    // Billing
    setLoadingBill(true);
    setBillError(false);
    api.get("/jonas-exports")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setBillingData(rows);
      })
      .catch(() => { setBillingData([]); setBillError(true); })
      .finally(() => setLoadingBill(false));
  }, [filter]);

  // Work order type map for billing labels
  useEffect(() => {
    api.get("/workorder-types")
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((t) => { map[t.id] = t.type; });
        setWorkOrderTypes(map);
      })
      .catch(() => { /* non-critical. billing labels fall back to "Unknown" */ });
  }, []);

  const loading = loadingWO || loadingVio || loadingArch || loadingBill;

  // ── Date filter helpers ───────────────────────────────────────────────────────
  const handleFilterChange = (e) => setFilter((p) => ({ ...p, [e.target.name]: e.target.value }));

  const filteredWO = useMemo(() => {
    const { startDate, endDate } = filter;
    return workOrdersData.filter((o) => {
      const d = new Date(o.created_at);
      const s = startDate ? new Date(startDate) : null;
      const e = endDate   ? new Date(endDate)   : null;
      return (!s || d >= s) && (!e || d <= e);
    });
  }, [workOrdersData, filter]);

  // violations and arch are already filtered server-side; billing filtered client-side
  const filteredBilling = useMemo(() => {
    const { startDate, endDate } = filter;
    return billingData.filter((b) => {
      if (!b.sent_at) return false;
      const d = new Date(b.sent_at);
      const s = startDate ? new Date(startDate) : null;
      const e = endDate   ? new Date(endDate)   : null;
      return (!s || d >= s) && (!e || d <= e);
    });
  }, [billingData, filter]);

  // ── KPI computations ──────────────────────────────────────────────────────────
  const woOpen   = filteredWO.filter((o) => !CLOSED_STATUSES.has((o.status || "").toLowerCase())).length;
  const woTotal  = filteredWO.length;

  const vioOpen  = violationsData.filter((v) => !CLOSED_STATUSES.has((v.status || "").toLowerCase())).length;
  const vioTotal = violationsData.length;

  const archPending = archData.filter((r) => !CLOSED_STATUSES.has((r.status || "").toLowerCase())).length;
  const archTotal   = archData.length;

  const billedTotal = filteredBilling.reduce((s, b) => s + (b.billable ? parseFloat(b.price || 0) : 0), 0);
  const billedThisMonth = useMemo(() => {
    const now = new Date();
    return filteredBilling
      .filter((b) => {
        const d = new Date(b.sent_at);
        return b.billable && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((s, b) => s + parseFloat(b.price || 0), 0);
  }, [filteredBilling]);

  const avgResolutionDays = useMemo(() => {
    const resolved = filteredWO.filter((o) => o.created_at && o.resolved_at);
    if (!resolved.length) return 0;
    const total = resolved.reduce((s, o) => s + (new Date(o.resolved_at) - new Date(o.created_at)) / 86400000, 0);
    return (total / resolved.length).toFixed(1);
  }, [filteredWO]);

  // ── Cross-module activity trend (last 12 months) ──────────────────────────────
  const monthlyActivityData = useMemo(() => {
    const keys = last12MonthKeys();
    const buildCounts = (rows, dateField) => {
      const c = {};
      rows.forEach((r) => {
        const k = buildMonthlyKey(r[dateField]);
        if (k) c[k] = (c[k] || 0) + 1;
      });
      return keys.map((k) => c[k] || 0);
    };
    return {
      labels: keys,
      wo:   buildCounts(filteredWO, "created_at"),
      vio:  buildCounts(violationsData, "created_at"),
      arch: buildCounts(archData, "created_at"),
    };
  }, [filteredWO, violationsData, archData]);

  // ── Billing monthly trend ──────────────────────────────────────────────────────
  const billingMonthly = useMemo(() => {
    const c = {};
    filteredBilling.forEach((b) => {
      if (!b.billable || !b.sent_at) return;
      const k = buildMonthlyKey(b.sent_at);
      if (k) c[k] = (c[k] || 0) + parseFloat(b.price || 0);
    });
    const keys = Object.keys(c).sort().slice(-12);
    return { labels: keys, data: keys.map((k) => parseFloat(c[k].toFixed(2))) };
  }, [filteredBilling]);

  // ── Status doughnuts ──────────────────────────────────────────────────────────
  const buildStatusDoughnut = (rows) => {
    const c = {};
    rows.forEach((r) => { const s = r.status || "Unknown"; c[s] = (c[s] || 0) + 1; });
    const entries = Object.entries(c).sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([k]) => k),
      datasets: [{
        data: entries.map(([, v]) => v),
        backgroundColor: entries.map((_, i) => softColors[i % softColors.length]),
        borderWidth: 2, borderColor: "#fff", hoverBorderColor: "#fff",
      }],
    };
  };

  const woDoughnut   = useMemo(() => buildStatusDoughnut(filteredWO),     [filteredWO]);
  const vioDoughnut  = useMemo(() => buildStatusDoughnut(violationsData),  [violationsData]);
  const archDoughnut = useMemo(() => buildStatusDoughnut(archData),        [archData]);

  // ── Top types (horizontal bars) ───────────────────────────────────────────────
  const topWOTypes = useMemo(() => {
    const c = {};
    filteredWO.forEach((o) => { const t = o.type_name || o.type || "Unknown"; c[t] = (c[t] || 0) + 1; });
    const top = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: top.map(([k]) => k), data: top.map(([, v]) => v) };
  }, [filteredWO]);

  const topVioTypes = useMemo(() => {
    const c = {};
    violationsData.forEach((v) => { const t = v.violation_type_title || "Unknown"; c[t] = (c[t] || 0) + 1; });
    const top = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: top.map(([k]) => k), data: top.map(([, v]) => v) };
  }, [violationsData]);

  const topBillingTypes = useMemo(() => {
    const c = {};
    filteredBilling.forEach((b) => {
      if (!b.billable) return;
      const name = workOrderTypes[b.work_order_type] || "Unknown";
      c[name] = (c[name] || 0) + parseFloat(b.price || 0);
    });
    const top = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: top.map(([k]) => k), data: top.map(([, v]) => parseFloat(v.toFixed(2))) };
  }, [filteredBilling, workOrderTypes]);

  // ── Open vs Closed stacked. WO ───────────────────────────────────────────────
  const woOpenClosed = useMemo(() => {
    const keys = last12MonthKeys();
    const open = {}, closed = {};
    filteredWO.forEach((o) => {
      const k = buildMonthlyKey(o.created_at);
      if (!k) return;
      if (CLOSED_STATUSES.has((o.status || "").toLowerCase())) closed[k] = (closed[k] || 0) + 1;
      else open[k] = (open[k] || 0) + 1;
    });
    return { labels: keys, open: keys.map((k) => open[k] || 0), closed: keys.map((k) => closed[k] || 0) };
  }, [filteredWO]);

  // ── Resolution time histogram ─────────────────────────────────────────────────
  const resolutionHistogram = useMemo(() => {
    const counts = Array(6).fill(0);
    filteredWO.forEach((o) => {
      if (!o.created_at || !o.resolved_at) return;
      const days = (new Date(o.resolved_at) - new Date(o.created_at)) / 86400000;
      counts[bucketDays(days)]++;
    });
    return counts;
  }, [filteredWO]);

  // ── Day-of-week ───────────────────────────────────────────────────────────────
  const dowCounts = useMemo(() => {
    const counts = Array(7).fill(0);
    filteredWO.forEach((o) => { const d = o.created_at ? new Date(o.created_at) : null; if (d) counts[d.getDay()]++; });
    return counts;
  }, [filteredWO]);

  // ── Most common WO type by month ──────────────────────────────────────────────
  const monthlyDataByYear      = useMemo(() => processYearlyMonthly(filteredWO, "created_at"), [filteredWO]);
  const mostCommonTypesByMonth  = useMemo(() => getMostCommonTypeByMonthByYear(filteredWO), [filteredWO]);
  const selectedYearData = viewMode !== "all" && monthlyDataByYear[viewMode]
    ? monthlyDataByYear[viewMode]
    : new Array(12).fill(0);
  const forecastCount = forecastNextMonth(selectedYearData);

  // ── New: violations monthly trend (last 12 months) ───────────────────────────
  const vioMonthly = useMemo(() => {
    const keys = last12MonthKeys();
    const c = {};
    violationsData.forEach((v) => {
      const k = buildMonthlyKey(v.created_at);
      if (k) c[k] = (c[k] || 0) + 1;
    });
    return {
      labels: keys,
      data: keys.map((k) => c[k] || 0),
    };
  }, [violationsData]);

  // ── New: top 8 arch request types ────────────────────────────────────────────
  const topArchTypes = useMemo(() => {
    const c = {};
    archData.forEach((r) => {
      const t = r.type_name || "Unknown";
      c[t] = (c[t] || 0) + 1;
    });
    const top = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { labels: top.map(([k]) => k), data: top.map(([, v]) => v) };
  }, [archData]);

  // ── Export ─────────────────────────────────────────────────────────────────────
  const exportReport = (fmt) => {
    if (!filteredWO.length) { alert("No records found in the selected date range."); return; }
    const fileName = `report_work_orders_${new Date().toISOString()}`;
    if (fmt === "CSV") {
      const headers = Object.keys(filteredWO[0]);
      const rows = [headers.join(","), ...filteredWO.map((item) =>
        headers.map((h) => `"${String(item[h] || "").replace(/"/g, '""')}"`).join(",")
      )];
      const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `${fileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Work Orders Report", 14, 20);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
      const rawHeaders = Object.keys(filteredWO[0] || {});
      const headers = rawHeaders.map((h) => h.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()));
      doc.autoTable({
        head: [headers],
        body: filteredWO.map((item) => rawHeaders.map((k) => item[k] || "")),
        styles: { font: "courier", fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [47, 99, 173], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        startY: 35, margin: { left: 14, right: 14 }, theme: "grid",
      });
      doc.save(`${fileName}.pdf`);
    }
  };

  // ═══════════════════════ RENDER ════════════════════════════════════════════
  return (
    <Box sx={{ width: "100%" }}>

      {/* ── Filter card ──────────────────────────────────────────────────── */}
      <GlassCard sx={{ mb: 3 }}>
        <SectionHeader
          title="Overview Filters & Export"
          subtitle="Filter all modules by date range. Violations and Architectural Requests are filtered server-side."
        />
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Start Date" type="date" name="startDate"
              value={filter.startDate} onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="End Date" type="date" name="endDate"
              value={filter.endDate} onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent={{ xs: "stretch", md: "flex-end" }}>
              <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => exportReport("CSV")}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
                Export CSV
              </Button>
              <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={() => exportReport("PDF")}
                sx={{ backgroundColor: "#2f63ad", borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none",
                  "&:hover": { backgroundColor: "#275592" } }}>
                Export PDF
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </GlassCard>

      {/* ── Summary band: cross-module KPI chips (hidden when module inactive) ── */}
      {(() => {
        const kpiCards = [
          woActive  && { label: "Total Work Orders",   value: woTotal,   sublabel: `${woOpen} open`,     loading: loadingWO,   color: "#2f63ad", icon: <AssignmentIcon sx={{ fontSize: 18 }} /> },
          vioActive && { label: "Total Violations",    value: vioTotal,  sublabel: `${vioOpen} open`,    loading: loadingVio,  color: "#dc2626", icon: <GavelIcon sx={{ fontSize: 18 }} /> },
          archActive&& { label: "Total Arch Requests", value: archTotal, sublabel: `${archPending} pending`, loading: loadingArch, color: "#16a34a", icon: <ArchitectureIcon sx={{ fontSize: 18 }} /> },
                       { label: "Total Billed $",
                          value: `$${billedTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                          sublabel: `$${billedThisMonth.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} this month`,
                          loading: loadingBill, color: "#ea580c", icon: <AttachMoneyIcon sx={{ fontSize: 18 }} /> },
        ].filter(Boolean);
        const colSm = kpiCards.length >= 4 ? 3 : kpiCards.length === 3 ? 4 : kpiCards.length === 2 ? 6 : 12;
        return (
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {kpiCards.map((card) => (
              <Grid key={card.label} item xs={6} sm={colSm}>
                <KpiCard {...card} />
              </Grid>
            ))}
          </Grid>
        );
      })()}

      {/* ── Cross-module activity trend ───────────────────────────────────── */}
      <GlassCard sx={{ mb: 2.5 }}>
        <SectionHeader
          title="Cross-Module Activity Trend"
          subtitle="Monthly volume for Work Orders, Violations, and Architectural Requests over the last 12 months."
        />
        <Box sx={{ height: 320 }}>
          {loading ? <Skeleton variant="rounded" height={320} /> : (
            <Line
              data={{
                labels: monthlyActivityData.labels,
                datasets: [
                  woActive   && { label: "Work Orders",   data: monthlyActivityData.wo,   borderColor: MODULE_COLORS.workOrders,   backgroundColor: "rgba(47,99,173,0.1)",  fill: true, tension: 0.3, pointRadius: 3 },
                  vioActive  && { label: "Violations",    data: monthlyActivityData.vio,  borderColor: MODULE_COLORS.violations,   backgroundColor: "rgba(220,38,38,0.08)", fill: true, tension: 0.3, pointRadius: 3 },
                  archActive && { label: "Arch Requests", data: monthlyActivityData.arch, borderColor: MODULE_COLORS.archRequests, backgroundColor: "rgba(34,197,94,0.08)", fill: true, tension: 0.3, pointRadius: 3 },
                ].filter(Boolean),
              }}
              options={lineOptions}
            />
          )}
        </Box>
      </GlassCard>

      <Divider sx={{ mb: 4 }} />

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1: WORK ORDERS
      ══════════════════════════════════════════════════════════════════════ */}
      {woActive && <ModuleSection
        title="Work Orders"
        icon={AssignmentIcon}
        accentColor="#2f63ad"
        bgColor="rgba(47,99,173,0.06)"
      >
        {woError && (
          <Alert severity="error" sx={{ mb: 2 }}>Work order data could not be loaded.</Alert>
        )}
        {/* KPI row */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Total Work Orders" value={woTotal} sublabel="in selected range" loading={loadingWO} color="#2f63ad" icon={<AssignmentIcon sx={{ fontSize: 18 }} />} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Open Work Orders" value={woOpen} sublabel={`${woTotal ? Math.round((woOpen / woTotal) * 100) : 0}% of total`} loading={loadingWO} color="#2f63ad" icon={<PendingActionsIcon sx={{ fontSize: 18 }} />} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Avg Resolution Days" value={`${avgResolutionDays}d`} sublabel="days to close" loading={loadingWO} color="#7c3aed" icon={<ScheduleIcon sx={{ fontSize: 18 }} />} />
          </Grid>
        </Grid>

        {/* Status doughnut + Open vs Closed stacked bar */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={5}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Work Order Status" subtitle="Distribution of current statuses." />
              <Box sx={{ height: 300, maxWidth: 360, mx: "auto" }}>
                {loadingWO ? <Skeleton variant="rounded" height={300} /> : woDoughnut.labels.length ? (
                  <Doughnut data={woDoughnut} options={doughnutOptions} />
                ) : (
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>
                )}
              </Box>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Open vs Closed by Month" subtitle="Stacked monthly breakdown for the last 12 months." />
              <Box sx={{ height: 300 }}>
                {loadingWO ? <Skeleton variant="rounded" height={300} /> : woOpenClosed.labels.length ? (
                  <Bar
                    data={{
                      labels: woOpenClosed.labels,
                      datasets: [
                        { label: "Open",   data: woOpenClosed.open,   backgroundColor: "rgba(220,38,38,0.62)",  borderRadius: 6 },
                        { label: "Closed", data: woOpenClosed.closed, backgroundColor: "rgba(34,197,94,0.62)",  borderRadius: 6 },
                      ],
                    }}
                    options={stackedBarOptions}
                  />
                ) : <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>}
              </Box>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Resolution histogram + Day of week */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={6}>
            <GlassCard>
              <SectionHeader title="Resolution Time Distribution" subtitle="How long resolved work orders took to close." />
              <Box sx={{ height: 300 }}>
                {loadingWO ? <Skeleton variant="rounded" height={300} /> : (
                  <Bar
                    data={{
                      labels: RESOLUTION_BUCKETS,
                      datasets: [{ label: "Work Orders", data: resolutionHistogram,
                        backgroundColor: RESOLUTION_BUCKETS.map((_, i) => softColors[i % softColors.length]),
                        borderRadius: 8, barThickness: 28 }],
                    }}
                    options={hBarOptions}
                  />
                )}
              </Box>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <GlassCard>
              <SectionHeader title="Work Orders by Day of Week" subtitle="Which days see the most work order submissions." />
              <Box sx={{ height: 300 }}>
                {loadingWO ? <Skeleton variant="rounded" height={300} /> : (
                  <Bar
                    data={{
                      labels: DAY_LABELS,
                      datasets: [{ label: "Work Orders", data: dowCounts,
                        backgroundColor: DAY_LABELS.map((_, i) => softColors[i % softColors.length]),
                        borderRadius: 8, barThickness: 40 }],
                    }}
                    options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        datalabels: {
                          color: "#223142", backgroundColor: "rgba(255,255,255,0.92)",
                          borderColor: "rgba(0,0,0,0.12)", borderWidth: 1, borderRadius: 4,
                          font: { weight: "bold", size: 11 }, padding: 4,
                          anchor: "end", align: "top",
                          display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
                        },
                      },
                      scales: {
                        x: { grid: { display: false } },
                        y: { beginAtZero: true, grid: { color: "rgba(47,99,173,0.08)" } },
                      },
                    }}
                  />
                )}
              </Box>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Top 8 WO types. full width */}
        <GlassCard sx={{ mb: 2.5 }}>
          <SectionHeader title="Top 8 Work Order Types" subtitle="Highest-volume work order categories." />
          <Box sx={{ height: Math.max(240, topWOTypes.labels.length * 40) }}>
            {loadingWO ? <Skeleton variant="rounded" height={240} /> : topWOTypes.labels.length ? (
              <Bar
                data={{ labels: topWOTypes.labels, datasets: [{ label: "Work Orders", data: topWOTypes.data,
                  backgroundColor: "rgba(47,99,173,0.68)", borderRadius: 8, barThickness: 22 }] }}
                options={hBarOptions}
              />
            ) : <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>}
          </Box>
        </GlassCard>

        {/* Yearly trend bar. full width */}
        <GlassCard sx={{ mb: 2.5 }}>
          <SectionHeader title="Work Orders Trend by Year" subtitle="Monthly work order volume grouped by year." />
          <Box sx={{ height: 320 }}>
            {loadingWO ? <Skeleton variant="rounded" height={320} /> : (
              <Bar
                data={{
                  labels: MONTH_LABELS,
                  datasets: Object.entries(monthlyDataByYear).map(([yr, data], idx) => ({
                    label: yr, data,
                    backgroundColor: [
                      "rgba(47,99,173,0.75)","rgba(82,137,214,0.65)","rgba(118,164,226,0.6)",
                      "rgba(163,196,243,0.75)","rgba(99,132,255,0.55)","rgba(54,162,235,0.55)",
                    ][idx % 6],
                    borderRadius: 8, maxBarThickness: 34,
                  })),
                }}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { position: "top" }, datalabels: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "rgba(47,99,173,0.08)" } },
                  },
                }}
              />
            )}
          </Box>
        </GlassCard>

        {/* Most common WO type by month + forecast. full width */}
        <GlassCard sx={{ mb: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" }, gap: 2, mb: 2.5 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                Most Common Work Order Type by Month
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Review the most frequent work order type for each month.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={`Forecast next month: ${forecastCount}`}
                size="small"
                sx={{ fontWeight: 700, backgroundColor: "rgba(47,99,173,0.1)", color: "#2f63ad", borderRadius: "10px" }}
              />
              <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small"
                sx={{ flexWrap: "wrap", gap: 1,
                  "& .MuiToggleButton-root": { border: "1px solid rgba(47,99,173,0.12)", borderRadius: "10px !important",
                    textTransform: "none", fontWeight: 600, color: "#5f6b7a", px: 1.75, minHeight: 36 },
                  "& .MuiToggleButton-root.Mui-selected": { color: "#2f63ad", backgroundColor: "rgba(47,99,173,0.08)",
                    borderColor: "rgba(47,99,173,0.18)" } }}>
                <ToggleButton value="all">All</ToggleButton>
                {Object.keys(mostCommonTypesByMonth).map((yr) => (
                  <ToggleButton key={yr} value={yr}>{yr}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Box>

          <Grid container spacing={2.5}>
            {MONTH_LABELS.map((month, idx) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={idx}>
                <Card sx={{ borderRadius: "16px", border: "1px solid rgba(47,99,173,0.08)",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.04)", backgroundColor: "rgba(255,255,255,0.98)",
                  p: 2, minHeight: viewMode === "all" ? 132 : 104, transition: "all 0.2s ease",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(0,0,0,0.07)" } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1.25 }}>{month}</Typography>
                  {viewMode === "all" ? (
                    <Stack spacing={0.75}>
                      {Object.entries(mostCommonTypesByMonth).map(([yr, values]) => (
                        <Box key={yr} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 700, minWidth: 36 }}>{yr}</Typography>
                          <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{values[idx]}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600, wordBreak: "break-word" }}>
                      {mostCommonTypesByMonth[viewMode]?.[idx] || "N/A"}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </GlassCard>
      </ModuleSection>}

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2: VIOLATIONS
      ══════════════════════════════════════════════════════════════════════ */}
      {vioActive && <ModuleSection
        title="Violations"
        icon={GavelIcon}
        accentColor="#dc2626"
        bgColor="rgba(220,38,38,0.05)"
      >
        {vioError && (
          <Alert severity="error" sx={{ mb: 2 }}>Violations data could not be loaded.</Alert>
        )}
        {/* KPI row */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6}>
            <KpiCard label="Total Violations" value={vioTotal} sublabel="in selected range" loading={loadingVio} color="#dc2626" icon={<GavelIcon sx={{ fontSize: 18 }} />} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <KpiCard label="Open Violations" value={vioOpen} sublabel={`${vioTotal ? Math.round((vioOpen / vioTotal) * 100) : 0}% of total`} loading={loadingVio} color="#dc2626" icon={<WarningAmberIcon sx={{ fontSize: 18 }} />} />
          </Grid>
        </Grid>

        {/* Status doughnut + Top 8 violation types */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={5}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Violation Status" subtitle="Distribution of current statuses." />
              <Box sx={{ height: 300, maxWidth: 360, mx: "auto" }}>
                {loadingVio ? <Skeleton variant="rounded" height={300} /> : vioDoughnut.labels.length ? (
                  <Doughnut data={vioDoughnut} options={doughnutOptions} />
                ) : (
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>
                )}
              </Box>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Top 8 Violation Types" subtitle="Most frequent violation categories." />
              <Box sx={{ height: Math.max(240, topVioTypes.labels.length * 40) }}>
                {loadingVio ? <Skeleton variant="rounded" height={240} /> : topVioTypes.labels.length ? (
                  <Bar
                    data={{ labels: topVioTypes.labels, datasets: [{ label: "Violations", data: topVioTypes.data,
                      backgroundColor: "rgba(220,38,38,0.62)", borderRadius: 8, barThickness: 22 }] }}
                    options={hBarOptions}
                  />
                ) : <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>}
              </Box>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Monthly violations trend. full width */}
        <GlassCard sx={{ mb: 2.5 }}>
          <SectionHeader title="Monthly Violations Trend" subtitle="Violation volume over the last 12 months." />
          <Box sx={{ height: 300 }}>
            {loadingVio ? <Skeleton variant="rounded" height={300} /> : (
              <Line
                data={{
                  labels: vioMonthly.labels,
                  datasets: [
                    { label: "Violations", data: vioMonthly.data, borderColor: MODULE_COLORS.violations,
                      backgroundColor: "rgba(220,38,38,0.08)", fill: true, tension: 0.3, pointRadius: 3 },
                  ],
                }}
                options={lineOptions}
              />
            )}
          </Box>
        </GlassCard>
      </ModuleSection>}

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3: ARCHITECTURAL REQUESTS
      ══════════════════════════════════════════════════════════════════════ */}
      {archActive && <ModuleSection
        title="Architectural Requests"
        icon={ArchitectureIcon}
        accentColor="#16a34a"
        bgColor="rgba(22,163,74,0.05)"
      >
        {archError && (
          <Alert severity="error" sx={{ mb: 2 }}>Architectural requests data could not be loaded.</Alert>
        )}
        {/* KPI row */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6}>
            <KpiCard label="Total Arch Requests" value={archTotal} sublabel="in selected range" loading={loadingArch} color="#16a34a" icon={<ArchitectureIcon sx={{ fontSize: 18 }} />} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <KpiCard label="Pending Arch Requests" value={archPending} sublabel={`${archTotal ? Math.round((archPending / archTotal) * 100) : 0}% of total`} loading={loadingArch} color="#16a34a" icon={<HourglassBottomIcon sx={{ fontSize: 18 }} />} />
          </Grid>
        </Grid>

        {/* Status doughnut + Top arch request types */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={5}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Arch Request Status" subtitle="Distribution of current statuses." />
              <Box sx={{ height: 300, maxWidth: 360, mx: "auto" }}>
                {loadingArch ? <Skeleton variant="rounded" height={300} /> : archDoughnut.labels.length ? (
                  <Doughnut data={archDoughnut} options={doughnutOptions} />
                ) : (
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>
                )}
              </Box>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Top Arch Request Types" subtitle="Most frequent architectural request categories." />
              <Box sx={{ height: Math.max(240, topArchTypes.labels.length * 40) }}>
                {loadingArch ? <Skeleton variant="rounded" height={240} /> : topArchTypes.labels.length ? (
                  <Bar
                    data={{ labels: topArchTypes.labels, datasets: [{ label: "Arch Requests", data: topArchTypes.data,
                      backgroundColor: "rgba(22,163,74,0.62)", borderRadius: 8, barThickness: 22 }] }}
                    options={hBarOptions}
                  />
                ) : <Typography variant="body2" sx={{ color: "#6b778c" }}>No data.</Typography>}
              </Box>
            </GlassCard>
          </Grid>
        </Grid>
      </ModuleSection>}

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4: BILLING
      ══════════════════════════════════════════════════════════════════════ */}
      <ModuleSection
        title="Billing"
        icon={AttachMoneyIcon}
        accentColor="#ea580c"
        bgColor="rgba(234,88,12,0.05)"
      >
        {billError && (
          <Alert severity="error" sx={{ mb: 2 }}>Billing data could not be loaded.</Alert>
        )}
        {/* KPI row */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6}>
            <KpiCard
              label="Total Billed ($)"
              value={`$${billedTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              sublabel="in selected range"
              loading={loadingBill}
              color="#ea580c"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <KpiCard
              label="Billed This Month ($)"
              value={`$${billedThisMonth.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              sublabel="current calendar month"
              loading={loadingBill}
              color="#ea580c"
            />
          </Grid>
        </Grid>

        {/* Monthly billing revenue + Revenue by WO type */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={7}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Monthly Billing Revenue" subtitle="Total billed amount per month (last 12 months)." />
              <Box sx={{ height: 280 }}>
                {loadingBill ? <Skeleton variant="rounded" height={280} /> : billingMonthly.labels.length ? (
                  <Bar
                    data={{
                      labels: billingMonthly.labels,
                      datasets: [{ label: "Billed ($)", data: billingMonthly.data,
                        backgroundColor: "rgba(249,115,22,0.65)", borderRadius: 8, maxBarThickness: 34 }],
                    }}
                    options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        datalabels: { display: false },
                        tooltip: { callbacks: { label: (ctx) => `$${ctx.raw.toLocaleString()}` } },
                      },
                      scales: {
                        x: { grid: { display: false } },
                        y: { beginAtZero: true, grid: { color: "rgba(47,99,173,0.08)" },
                          ticks: { callback: (v) => `$${v.toLocaleString()}` } },
                      },
                    }}
                  />
                ) : <Typography variant="body2" sx={{ color: "#6b778c" }}>No billing data.</Typography>}
              </Box>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <GlassCard sx={{ height: "100%" }}>
              <SectionHeader title="Revenue by Work Order Type" subtitle="Top billed work order categories." />
              <Box sx={{ height: 280 }}>
                {loadingBill ? <Skeleton variant="rounded" height={280} /> : topBillingTypes.labels.length ? (
                  <Bar
                    data={{
                      labels: topBillingTypes.labels,
                      datasets: [{ label: "Billed ($)", data: topBillingTypes.data,
                        backgroundColor: topBillingTypes.labels.map((_, i) => softColors[i % softColors.length]),
                        borderRadius: 8, barThickness: 20 }],
                    }}
                    options={{
                      ...hBarOptions,
                      plugins: {
                        ...hBarOptions.plugins,
                        datalabels: { ...hBarOptions.plugins.datalabels,
                          formatter: (v) => `$${v.toLocaleString()}` },
                        tooltip: { callbacks: { label: (ctx) => `$${ctx.raw.toLocaleString()}` } },
                      },
                    }}
                  />
                ) : <Typography variant="body2" sx={{ color: "#6b778c" }}>No billing data.</Typography>}
              </Box>
            </GlassCard>
          </Grid>
        </Grid>
      </ModuleSection>

    </Box>
  );
};

export default OverviewWorkOrders;
